import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Box,
} from '@mui/material';
import { TertiaryButton, SecondaryButton } from '../common/Buttons';
import { getLowStockItems } from '../../services/dashboard';
import { useNavigate } from 'react-router-dom';

const LOW_STOCK_INVENTORY_LINK = '/inventory?low_stock=true';

/**
 * Single place for low (consumable) stock display.
 * - Pass items + loading to use parent-fetched data (e.g. Activity Dashboard).
 * - Omit items to fetch here (e.g. DashboardPage).
 * variant: 'compact' = card list (shows maxItems then "Show more..."), 'table' = full table (default).
 * maxItems: when variant=compact, show at most this many then "Show more..." (default 5).
 */
function LowStockItems({ items: itemsProp, loading: loadingProp, variant = 'table', maxItems = 5, totalCount }) {
    const [items, setItems] = useState(itemsProp ?? []);
    const [loading, setLoading] = useState(loadingProp ?? true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const isControlled = itemsProp !== undefined;

    useEffect(() => {
        if (isControlled) {
            setItems(Array.isArray(itemsProp) ? itemsProp : []);
            setLoading(Boolean(loadingProp));
            return;
        }
        const fetchItems = async () => {
            try {
                const data = await getLowStockItems();
                setItems(Array.isArray(data) ? data : []);
            } catch (err) {
                setError('Failed to load low stock items');
            } finally {
                setLoading(false);
            }
        };
        fetchItems();
    }, [isControlled, itemsProp, loadingProp]);

    const goToItem = (item) => {
        if (item.device_type || item.device_model) {
            navigate(`/inventory/equipment/${item.id}`);
        } else {
            navigate(`/inventory/consumables/${item.id}`);
        }
    };

    if (!isControlled && loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (!isControlled && error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const displayItems = isControlled ? (Array.isArray(itemsProp) ? itemsProp : []) : items;
    const compactItems = variant === 'compact' ? displayItems.slice(0, maxItems) : displayItems;
    // Use server-side totalCount if provided (more accurate when items are paginated)
    const realTotal = totalCount ?? displayItems.length;
    const hasMore = variant === 'compact' && realTotal > maxItems;

    if (variant === 'compact') {
        return (
            <>
                {displayItems.length === 0 ? (
                    <Typography variant="body1">No low stock items</Typography>
                ) : (
                    <Box>
                        {compactItems.map((item) => (
                            <Box
                                key={item.id}
                                mb={2}
                                sx={{
                                    cursor: 'pointer',
                                    padding: 1,
                                    borderRadius: 1,
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                                onClick={() => goToItem(item)}
                            >
                                <Typography variant="subtitle1">{item.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Current: {item.current_quantity} | Min: {item.min_stock_level}
                                </Typography>
                            </Box>
                        ))}
                        {hasMore && (
                            <TertiaryButton
                                size="small"
                                fullWidth
                                onClick={() => navigate(LOW_STOCK_INVENTORY_LINK)}
                                sx={{ mt: 1 }}
                            >
                                Show more… ({realTotal - maxItems} more)
                            </TertiaryButton>
                        )}
                        {displayItems.length > 0 && !hasMore && (
                            <TertiaryButton
                                size="small"
                                fullWidth
                                onClick={() => navigate(LOW_STOCK_INVENTORY_LINK)}
                                sx={{ mt: 1 }}
                            >
                                View in Inventory
                            </TertiaryButton>
                        )}
                    </Box>
                )}
            </>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Current Stock</TableCell>
                        <TableCell>Min Stock</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {displayItems.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} align="center">
                                No low stock items
                            </TableCell>
                        </TableRow>
                    ) : (
                        displayItems.map((item) => (
                            <TableRow
                                key={item.id}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                            >
                                <TableCell onClick={() => goToItem(item)}>{item.name}</TableCell>
                                <TableCell onClick={() => goToItem(item)}>{item.current_quantity}</TableCell>
                                <TableCell onClick={() => goToItem(item)}>{item.min_stock_level}</TableCell>
                                <TableCell onClick={() => goToItem(item)}>
                                    <Typography
                                        color={
                                            item.current_quantity <= item.min_stock_level ? 'error' : 'warning'
                                        }
                                    >
                                        {item.current_quantity <= item.min_stock_level ? 'Reorder Now' : 'Low Stock'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <SecondaryButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/inventory');
                                        }}
                                    >
                                        Manage
                                    </SecondaryButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default LowStockItems; 